import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

/**
 * StickyNav — barre de navigation fixe verre translucide.
 * Props:
 *   links        — tableau { label, href } pour les ancres
 *   homeLabel    — texte du lien gauche (défaut "COCON")
 *   homePath     — route du lien gauche (défaut "/")
 *   showHome     — afficher le lien gauche (défaut true)
 *   triggerRatio — fraction de vh pour apparaître (défaut 0.85)
 */
const StickyNav = ({
  links = [],
  homeLabel = 'COCON',
  homePath = '/',
  showHome = true,
  triggerRatio = 0.85,
}) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > window.innerHeight * triggerRatio);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [triggerRatio]);

  const scrollTo = (href) => (e) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <nav
      data-testid="sticky-nav"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        transform: visible ? 'translateY(0)' : 'translateY(-100%)',
        transition: 'transform 0.45s cubic-bezier(0.4, 0, 0.2, 1)',
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
        backgroundColor: 'rgba(245, 240, 232, 0.82)',
        borderBottom: '1px solid rgba(197, 160, 89, 0.18)',
        boxShadow: '0 1px 24px rgba(63, 42, 36, 0.06)',
      }}
    >
      <div
        style={{
          maxWidth: '72rem',
          margin: '0 auto',
          padding: '0 1.5rem',
          height: '52px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem',
        }}
      >
        {/* Lien gauche (home ou label) */}
        {showHome && (
          <Link
            to={homePath}
            data-testid="sticky-nav-home"
            style={{
              fontFamily: 'serif',
              fontSize: '0.85rem',
              letterSpacing: '0.2em',
              color: '#3F2A24',
              textDecoration: 'none',
              flexShrink: 0,
              opacity: 0.85,
              transition: 'opacity 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
            onMouseLeave={e => { e.currentTarget.style.opacity = '0.85'; }}
          >
            {homeLabel}
          </Link>
        )}

        {/* Liens de section - Espacement respirant */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            overflowX: 'auto',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          {links.map(({ label, href }) => (
            <a
              key={href}
              href={href}
              onClick={scrollTo(href)}
              data-testid={`sticky-nav-${href.replace('#', '')}`}
              style={{
                position: 'relative',
                fontSize: '0.6rem',
                letterSpacing: '0.25em',
                textTransform: 'uppercase',
                color: '#5C4A3D',
                textDecoration: 'none',
                padding: '0.5rem 1.2rem',
                whiteSpace: 'nowrap',
                transition: 'color 0.3s ease',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.color = '#C5A059';
                // Add gold underline
                const underline = e.currentTarget.querySelector('.nav-underline');
                if (underline) {
                  underline.style.transform = 'scaleX(1)';
                  underline.style.opacity = '1';
                }
              }}
              onMouseLeave={e => {
                e.currentTarget.style.color = '#5C4A3D';
                // Remove gold underline
                const underline = e.currentTarget.querySelector('.nav-underline');
                if (underline) {
                  underline.style.transform = 'scaleX(0)';
                  underline.style.opacity = '0';
                }
              }}
            >
              {label}
              {/* Subtle gold underline on hover */}
              <span
                className="nav-underline"
                style={{
                  position: 'absolute',
                  bottom: '0.25rem',
                  left: '1.2rem',
                  right: '1.2rem',
                  height: '1px',
                  backgroundColor: '#C5A059',
                  transform: 'scaleX(0)',
                  opacity: '0',
                  transition: 'transform 0.3s ease, opacity 0.3s ease',
                  transformOrigin: 'center',
                }}
              />
            </a>
          ))}
        </div>
      </div>
    </nav>
  );
};

export default StickyNav;
